import UnverifiedAccountContext, {
    UnverifiedAccounProvider,
    EUnverifiedAccountUiState,
} from '../../contexts/UnverifiedAccountContext';
import styles from './UnverifiedAccountScreen.module.scss';
import common from '../../util/scss/common.module.scss';
import { Redirect } from 'react-router';
import { ELDFlags, ERoutes } from '../../util/constants';
import { IconEnvelope } from '../../icons/Icons';
import API from '../../api/api';
import { FC, useContext, useEffect } from 'react';
import { Alert, Link, Snackbar } from '@mui/material';
import GlobalContext from '../../contexts/GlobalContext';
import PageLayoutV2 from '../../layout/PageLayoutV2/PageLayoutV2';
import VerifyWithPasscode from '../../components/VerifyWithPasscode/VerifyWithPasscode';
import { useFlags } from 'launchdarkly-react-client-sdk';

const UnverifiedWithEmail: FC = () => {
    const { language, fullLoginLink } = useContext(GlobalContext);
    const { email, requestVerificationEmail, showToaster } = useContext(UnverifiedAccountContext);

    const handleResendEmailClick = () => {
        API.post.trackEvent({
            event: 'Sign In || Verification | Verification hard stop | Button - Resend verification email',
        });
        requestVerificationEmail();
    };

    useEffect(() => {
        API.post.trackEvent({ event: 'Sign In || Verification | Verification hard stop' });
    }, []);

    return (
        <>
            <section className={common.icon}>
                <IconEnvelope />
            </section>
            <header>
                <h1 className={common.pageHeader}>{language.unverified.header}</h1>
            </header>
            <main>
                <p className={styles.email}>{email}</p>
            </main>
            <footer className={styles.footer}>
                <p>
                    {language.unverified.desc} <Link href={fullLoginLink}>{language.unverified.descCTA}</Link>
                </p>
            </footer>
            <section className={styles.resendEmail}>
                <Link role="button" onClick={handleResendEmailClick}>
                    {language.unverified.resendEmail}
                </Link>
            </section>
            <section className={styles.resendEmailSuccess}>
                <Snackbar open={showToaster}>
                    <Alert severity="success"> {language.unverified.resendSuccess}</Alert>
                </Snackbar>
            </section>
        </>
    );
};

const UnverifiedAccountUi: FC = () => {
    const { unverifiedAccountUiState, email, submitVerifyPasscode, resendVerifyPasscode } =
        useContext(UnverifiedAccountContext);
    const { [ELDFlags.useConsumerPasscodeVerification]: usePasscodeForVerification } = useFlags();

    switch (unverifiedAccountUiState) {
        case EUnverifiedAccountUiState.default:
        case EUnverifiedAccountUiState.routerParsed:
        case EUnverifiedAccountUiState.loading:
        case EUnverifiedAccountUiState.success:
            return (
                <>
                    {usePasscodeForVerification ? (
                        <VerifyWithPasscode
                            email={email}
                            onVerifyPasscode={submitVerifyPasscode}
                            onResendPasscode={resendVerifyPasscode}
                        />
                    ) : (
                        <UnverifiedWithEmail />
                    )}
                </>
            );
        case EUnverifiedAccountUiState.error:
            return <Redirect to={ERoutes.error} />;
        default:
            return (
                <>
                    {console.error(`Unverified Account UI state set incorrectly -- ${unverifiedAccountUiState}`)}
                    <Redirect to={ERoutes.error} />;
                </>
            );
    }
};

const UnverifiedAccountScreen: FC = () => {
    return (
        <UnverifiedAccounProvider>
            <PageLayoutV2>
                <UnverifiedAccountUi />
            </PageLayoutV2>
        </UnverifiedAccounProvider>
    );
};

export default UnverifiedAccountScreen;
